import React, { useState, useContext, createContext, ReactNode } from "react";

interface ReitsValuationState {
  valuationDate: string;
  leaseableArea: number;
  occupancy: number;
  avgRental: number;
  escalation: number;
  rentalInflow: number;
  noi: number;
  noiPercentage: number;
  columns: any[];
  cashFlowData: any[];
  errors: Record<string, string | number | boolean>;

  // Calculate Risk Premium
  location: number;
  tenantProfile: number;
  wale: number;
  buildingLife: number;
  vacancy: number;
  propertyType: number;
  esgCompliance: number;
  riskPremium: number;

  // Calculate WACC
  waccRange: number;
  capRate: number;
  costOfEquity: number;
  costOfDebt: number;
  weightOfEquity: number;
  weightOfDebt: number;

  // CARE Valuation
  waccDiscountRate: number;
  valuationCare: number;

  // User Valuation
  waccDiscountRateUser: number;
  valuationUser: number;
}

// Define context type
interface ReitsValuationContextType {
  evalState: ReitsValuationState;
  setEvalState: React.Dispatch<React.SetStateAction<ReitsValuationState>>;
}

// Provider props
interface ReitsValuationProviderProps {
  children: ReactNode;
}

const ReitsValuationContext = createContext<
  ReitsValuationContextType | undefined
>(undefined);

function useReitsValuationState(): ReitsValuationContextType {
  const valuationState = useContext(ReitsValuationContext);

  if (valuationState === undefined) {
    throw new Error(
      "useReitsValuationState must be used within ReitsValuationContextProvider"
    );
  }

  return valuationState;
}

const ReitsValuationContextProvider: React.FC<ReitsValuationProviderProps> = ({
  children,
}) => {
  const [evalState, setEvalState] = useState<ReitsValuationState>({
    valuationDate: "",
    leaseableArea: 0,
    occupancy: 0,
    avgRental: 0,
    escalation: 5,
    rentalInflow: 0,
    noi: 0,
    noiPercentage: 85,
    columns: [],
    cashFlowData: [],
    errors: {},

    // Calculate Risk Premium
    location: 0,
    tenantProfile: 0,
    wale: 0,
    buildingLife: 0,
    vacancy: 0,
    propertyType: 0,
    esgCompliance: 0,
    riskPremium: 0,

    // Calculate WACC
    waccRange: 0,
    capRate: 0,
    costOfEquity: 0,
    costOfDebt: 0,
    weightOfEquity: 0,
    weightOfDebt: 0,

    // CARE Valuation
    waccDiscountRate: 0,
    valuationCare: 0,

    // User Valuation
    waccDiscountRateUser: 0,
    valuationUser: 0,
  });

  const value: ReitsValuationContextType = { evalState, setEvalState };

  return (
    <ReitsValuationContext.Provider value={value}>
      {children}
    </ReitsValuationContext.Provider>
  );
};

export { ReitsValuationContextProvider, useReitsValuationState };
